import { useState } from 'react';
import { Controller, useForm, FieldValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { InputField } from '@shared/ui/input-field';
import { Button } from '@shared/ui/button';
import { AddEntity } from '@shared/ui/add-entity';
import ReactInputMask from 'react-input-mask';
import { useAppDispatch } from '@app/store/store';
import { setErrorNotification, setNotification } from '@app/store/notifications';
import { apiClient } from '../../../api/instance';
import { MyModal } from '@shared/ui/my-modal';

// Эту форму открывает пользователь, у которого ещё нет привязанной
// компании, поэтому предсказать страну (RU/KZ) заранее нельзя.
// Показываем универсальный лейбл «ИНН / БИН / ИИН» — он покрывает
// и российский ИНН, и казахстанские БИН (юрлица) и ИИН (ИП/физлица).
// Регекс /^\d{10}$|^\d{12}$/ совместим с обеими странами:
// RU ИНН — 10 или 12 цифр; KZ БИН/ИИН — всегда 12.
const TAX_LABEL_UNIVERSAL = 'ИНН / БИН / ИИН';

const requestCompanySchema = yup.object().shape({
  company_name: yup.string().required('Название компании обязательно'),
  inn: yup
    .string()
    .required(`${TAX_LABEL_UNIVERSAL} обязателен`)
    .matches(
      /^\d{10}$|^\d{12}$/,
      `${TAX_LABEL_UNIVERSAL} должен содержать 10 или 12 цифр`
    ),
  phone: yup
    .string()
    .required('Пожалуйста, введите номер телефона')
    .matches(
      /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/,
      'Пожалуйста, введите корректный номер телефона'
    ),
});

interface CompanyAccessRequestProps {
  userPhone?: string;
  /**
   * Колбек после успешной отправки заявки. Нужен, чтобы соседняя
   * карточка «Мои запросы и приглашения» (MyJoinRequestsCard в
   * Company.tsx) могла перетянуть свой список без F5: форма живёт
   * в feature-слое и про сайдкарту ничего не знает, поэтому
   * пробрасываем сигнал наверх.
   */
  onSuccess?: () => void;
}

const CompanyAccessRequest = ({
  userPhone,
  onSuccess,
}: CompanyAccessRequestProps) => {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(requestCompanySchema),
    defaultValues: {
      company_name: '',
      inn: '',
      phone: userPhone || '',
    },
  });

  const onSubmit = async (data: FieldValues) => {
    setIsSubmitting(true);
    try {
      await apiClient.post('/joinrequests/request_join/', data);
      dispatch(
        setNotification('Запрос успешно отправлен! Мы свяжемся с вами в ближайшее время.')
      );
      reset();
      setShowModal(false);
      // Сообщаем родителю, что заявка ушла, чтобы он мог
      // обновить связанные данные (см. Company.tsx →
      // MyJoinRequestsCard). Делаем это после reset/закрытия
      // модалки — пользователь сразу видит свежий список под формой.
      onSuccess?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMessage =
        err?.response?.data?.message ||
        'Произошла ошибка при отправке запроса. Попробуйте позже.';
      dispatch(setErrorNotification(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AddEntity text="компанию" isLink={false} onClick={() => setShowModal(true)} />
      <MyModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Запрос на добавление компании"
        description="Заполните форму, чтобы отправить запрос на добавление вашей компании"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
          <InputField
            label="Название компании"
            inputName="company_name"
            register={register}
            errors={errors}
            placeholder="Введите название компании"
            autoComplete="off"
          />
          <InputField
            label={TAX_LABEL_UNIVERSAL}
            inputName="inn"
            register={register}
            errors={errors}
            placeholder={`Введите ${TAX_LABEL_UNIVERSAL} (10 или 12 цифр)`}
            autoComplete="off"
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <InputField
                {...field}
                as={ReactInputMask}
                mask="+7 (***) ***-**-**"
                label="Телефон для связи"
                name="phone"
                errors={errors}
                placeholder="+7 (___) ___-__-__"
              />
            )}
          />
          <div className="pt-4 flex justify-end gap-2">
            <Button isWhite onClick={() => setShowModal(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Отправка…' : 'Отправить запрос'}
            </Button>
          </div>
        </form>
      </MyModal>
    </>
  );
};

export default CompanyAccessRequest;
